import { Container, Graphics } from 'pixi.js';

export type CellMark = {
  x: number;
  y: number;
  kind: 'actionable' | 'invalid' | 'focus';
  role?: 'from' | 'to';
};

/** Board is 8 wide (x) × 4 tall (y). Vertical mode remaps to 4 visual cols × 8 visual rows. */
export function visualGridSize(cols: number, rows: number, renderVertically: boolean) {
  if (renderVertically) {
    return { visualCols: rows, visualRows: cols };
  }
  return { visualCols: cols, visualRows: rows };
}

export function cellOrigin(
  x: number,
  y: number,
  cellSize: number,
  cols: number,
  rows: number,
  renderVertically: boolean
) {
  if (renderVertically) {
    // Matches CSS grid sort in Board.tsx: by x, then y descending within column
    const visualCol = rows - 1 - y;
    const visualRow = x;
    return {
      left: visualCol * cellSize,
      top: visualRow * cellSize,
      centerX: visualCol * cellSize + cellSize / 2,
      centerY: visualRow * cellSize + cellSize / 2,
    };
  }
  return {
    left: x * cellSize,
    top: y * cellSize,
    centerX: x * cellSize + cellSize / 2,
    centerY: y * cellSize + cellSize / 2,
  };
}

export function screenToBoardCell(
  localX: number,
  localY: number,
  cellSize: number,
  cols: number,
  rows: number,
  renderVertically: boolean
): { x: number; y: number } | null {
  if (cellSize <= 0) return null;
  const { visualCols, visualRows } = visualGridSize(cols, rows, renderVertically);
  const visualCol = Math.floor(localX / cellSize);
  const visualRow = Math.floor(localY / cellSize);
  if (visualCol < 0 || visualCol >= visualCols || visualRow < 0 || visualRow >= visualRows) {
    return null;
  }
  if (renderVertically) {
    return { x: visualRow, y: rows - 1 - visualCol };
  }
  return { x: visualCol, y: visualRow };
}

function drawCornerBrackets(
  gfx: Graphics,
  left: number,
  top: number,
  cellSize: number,
  color: number,
  alpha: number,
  lineWidth: number
) {
  const m = cellSize * 0.1;
  const len = cellSize * 0.2;
  const x = left + m;
  const y = top + m;
  const s = cellSize - m * 2;
  gfx.setStrokeStyle({ width: lineWidth, color, alpha, cap: 'square', join: 'miter' });
  gfx.moveTo(x, y + len).lineTo(x, y).lineTo(x + len, y);
  gfx.moveTo(x + s - len, y).lineTo(x + s, y).lineTo(x + s, y + len);
  gfx.moveTo(x, y + s - len).lineTo(x, y + s).lineTo(x + len, y + s);
  gfx.moveTo(x + s - len, y + s).lineTo(x + s, y + s).lineTo(x + s, y + s - len);
  gfx.stroke();
}

function drawRing(
  gfx: Graphics,
  left: number,
  top: number,
  cellSize: number,
  color: number,
  alpha: number,
  lineWidth: number
) {
  const inset = cellSize * 0.12;
  const radius = cellSize * 0.05;
  gfx.setStrokeStyle({ width: lineWidth, color, alpha, cap: 'round', join: 'round' });
  gfx.roundRect(
    left + inset,
    top + inset,
    cellSize - inset * 2,
    cellSize - inset * 2,
    radius
  );
  gfx.stroke();
}

export class Highlights extends Container {
  private gfx = new Graphics();

  constructor() {
    super();
    this.eventMode = 'none';
    this.addChild(this.gfx);
  }

  draw(
    marks: CellMark[],
    cellSize: number,
    cols: number,
    rows: number,
    renderVertically: boolean
  ) {
    this.gfx.clear();
    const line = Math.max(1.5, cellSize * 0.045);

    for (const mark of marks) {
      const { left, top } = cellOrigin(mark.x, mark.y, cellSize, cols, rows, renderVertically);

      if (mark.kind === 'focus') {
        const isTo = mark.role !== 'from';
        if (isTo) {
          this.gfx
            .rect(
              left + cellSize * 0.08,
              top + cellSize * 0.08,
              cellSize * 0.84,
              cellSize * 0.84
            )
            .fill({ color: 0xfbbf24, alpha: 0.1 });
        }
        drawCornerBrackets(
          this.gfx,
          left,
          top,
          cellSize,
          isTo ? 0xfbbf24 : 0x94a3b8,
          isTo ? 0.95 : 0.7,
          isTo ? line : Math.max(1.25, line * 0.85)
        );
        continue;
      }

      if (mark.kind === 'invalid') {
        drawRing(this.gfx, left, top, cellSize, 0xf87171, 0.85, line);
        continue;
      }

      drawRing(this.gfx, left, top, cellSize, 0xfbbf24, 0.7, line);
    }
  }
}
